import React, { useEffect, useState } from 'react';
import {
  EmailThread, ReplyClassification,
  getEmailThreads, syncEmailThreads, getSyncStatus, setThreadClassification, deleteEmailThread,
} from '../../api/emailThreads';
import { CLASSIFICATION_LABELS, CLASSIFICATION_ORDER } from './classificationLabels';
import { ReplyDialog } from '../communication/ReplyDialog';
import { ManualEntryDialog } from '../communication/ManualEntryDialog';

interface Props {
  mandateId: string;
  companyId: string;
}

/**
 * Zeigt die Kommunikation (gesendet + empfangen) zwischen einem Mandat
 * und einem konkreten Investor (Firma). Inkl. IMAP-Sync-Button und
 * Klassifizierung der Antworten.
 */
export function MatchCommunicationTab({ mandateId, companyId }: Props) {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<EmailThread | null>(null);
  const [manualOpen, setManualOpen] = useState(false);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const r = await getEmailThreads({ mandate_id: mandateId, company_id: companyId });
      setThreads(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadThreads(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mandateId, companyId]);

  const handleSync = async () => {
    setSyncBusy(true);
    setSyncMsg('Sync läuft im Hintergrund …');
    try {
      const r = await syncEmailThreads();
      if (r.alreadyRunning) setSyncMsg('Sync läuft bereits …');
      const startedAt = Date.now();
      const poll = async (): Promise<void> => {
        const status = await getSyncStatus();
        if (status.running && Date.now() - startedAt < 180_000) {
          setSyncMsg(`Sync läuft … ${status.imported} neu, ${status.skipped} bereits vorhanden`);
          setTimeout(poll, 3000);
          return;
        }
        if (status.error) setSyncMsg(`Fehler: ${status.error}`);
        else setSyncMsg(`Fertig: ${status.imported} neu, ${status.skipped} bereits vorhanden`);
        setSyncBusy(false);
        await loadThreads();
      };
      setTimeout(poll, 1500);
    } catch (e) {
      setSyncMsg(`Fehler: ${(e as Error).message}`);
      setSyncBusy(false);
    }
  };

  const handleClassify = async (threadId: string, value: ReplyClassification | null) => {
    setThreads(prev => prev.map(t =>
      t.id === threadId ? { ...t, classification: value, classification_manual: true } : t
    ));
    try { await setThreadClassification(threadId, value); }
    catch { await loadThreads(); }
  };

  const handleDelete = async (threadId: string) => {
    if (!confirm('Diesen Eintrag wirklich aus der Kommunikation löschen?')) return;
    const prev = threads;
    setThreads(p => p.filter(t => t.id !== threadId));
    try { await deleteEmailThread(threadId); }
    catch { setThreads(prev); }
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        paddingBottom: '14px', marginBottom: '16px',
        borderBottom: '1px solid var(--border)',
      }}>
        <button onClick={handleSync} disabled={syncBusy}
          style={{
            padding: '7px 14px', background: 'var(--surface)',
            border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
            fontSize: '12px', cursor: syncBusy ? 'wait' : 'pointer', color: 'var(--text2)',
          }}>
          {syncBusy ? 'Synchronisiere …' : 'Antworten abrufen (IMAP)'}
        </button>
        <button onClick={() => setManualOpen(true)}
          title="Telefonat, Treffen oder Notiz manuell eintragen"
          style={{
            padding: '7px 14px', background: 'var(--accent)', color: '#fff',
            border: 'none', borderRadius: 'var(--radius)',
            fontSize: '12px', fontWeight: 500, cursor: 'pointer',
          }}>
          + Manuell eintragen
        </button>
        {syncMsg && <span style={{ fontSize: '12px', color: 'var(--text3)' }}>{syncMsg}</span>}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
          {threads.filter(t => t.direction === 'sent').length} gesendet ·{' '}
          {threads.filter(t => t.direction === 'received').length} Antworten
        </span>
      </div>

      {loading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text3)' }}>Laden …</div>
      ) : threads.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>
          Noch keine Kommunikation mit diesem Investor für dieses Mandat.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {threads.map(t => {
            const isSent = t.direction === 'sent';
            const isManual = t.kind && t.kind !== 'email';
            const cls = t.classification ? CLASSIFICATION_LABELS[t.classification] : null;
            const partnerEmail = isSent ? t.to_email : t.from_email;
            const dateObj = new Date(t.occurred_at);
            const kindMeta = KIND_META[t.kind ?? 'email'];
            return (
              <div key={t.id} style={{
                padding: '14px 16px',
                background: isManual ? '#f5f9ff' : (isSent ? 'var(--surface2)' : '#fffbf5'),
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                borderLeft: `3px solid ${isManual ? '#1f6feb' : (isSent ? 'var(--border2)' : 'var(--accent)')}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' }}>
                      <span style={{
                        fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        color: isManual ? '#1f6feb' : (isSent ? 'var(--text3)' : 'var(--accent)'),
                      }}>
                        {kindMeta.icon} {isManual ? kindMeta.label : (isSent ? '→ Gesendet' : '← Antwort')}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 500 }}>
                        {t.contact_display_name || partnerEmail || '—'}
                      </span>
                    </div>
                    {!isManual && partnerEmail && (
                      <div style={{ fontSize: '11px', color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                        {partnerEmail}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: '5px' }}>
                      <strong>{isManual ? 'Thema:' : 'Betreff:'}</strong> {t.subject}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text3)', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {dateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}<br />
                        {dateObj.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {!isManual && (
                        <button type="button" onClick={() => setReplyTo(t)} title="Antworten"
                          style={{ padding: '3px 9px', fontSize: '11px',
                            background: 'var(--surface2)', border: '1px solid var(--border2)',
                            borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text2)' }}>
                          ↩ Antworten
                        </button>
                      )}
                      <button type="button" onClick={() => handleDelete(t.id)} title="Eintrag löschen"
                        style={{
                          background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer',
                          fontSize: '13px', color: 'var(--text3)', lineHeight: 1,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}>
                        ×
                      </button>
                    </div>
                    {!isSent && cls && (
                      <span title={t.classification_manual ? 'Manuell gesetzt' : 'Automatisch klassifiziert'}
                        style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 600,
                          background: cls.bg, color: cls.color,
                          border: t.classification_manual ? `1px solid ${cls.color}` : 'none',
                        }}>
                        {cls.icon} {cls.label}
                      </span>
                    )}
                  </div>
                </div>

                {!isSent && !isManual && (
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text3)' }}>Klassifizierung:</span>
                    <select
                      value={t.classification ?? ''}
                      onChange={e => handleClassify(t.id, (e.target.value || null) as ReplyClassification | null)}
                      style={{
                        padding: '2px 6px', fontSize: '11px',
                        border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
                        background: 'var(--surface)', cursor: 'pointer',
                      }}>
                      <option value="">— offen —</option>
                      {CLASSIFICATION_ORDER.map(key => (
                        <option key={key} value={key}>{CLASSIFICATION_LABELS[key].label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {t.body_text && (
                  <div style={{ marginTop: '8px' }}>
                    <button type="button"
                      onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '11px', color: 'var(--text3)', textDecoration: 'underline' }}>
                      {expanded === t.id ? (isManual ? 'Notiz einklappen' : 'E-Mail-Text einklappen') : (isManual ? 'Notiz anzeigen' : 'E-Mail-Text anzeigen')}
                    </button>
                    {expanded === t.id && (
                      <pre style={{
                        marginTop: '8px', padding: '10px 12px',
                        background: 'var(--surface)', borderRadius: 'var(--radius)',
                        fontSize: '11px', color: 'var(--text2)', lineHeight: 1.6,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        border: '1px solid var(--border)', maxHeight: '260px', overflow: 'auto',
                      }}>
                        {t.body_text}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ReplyDialog thread={replyTo} onClose={() => setReplyTo(null)} onSent={loadThreads} />
      <ManualEntryDialog
        isOpen={manualOpen}
        mandateId={mandateId}
        companyId={companyId}
        onClose={() => setManualOpen(false)}
        onSaved={loadThreads}
      />
    </div>
  );
}

const KIND_META: Record<string, { icon: string; label: string }> = {
  email:   { icon: '✉',  label: 'E-Mail' },
  phone:   { icon: '📞', label: 'Telefonat' },
  meeting: { icon: '🤝', label: 'Treffen' },
  note:    { icon: '📝', label: 'Notiz' },
};
